import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Col, Row, Tooltip, Typography } from 'antd';
import { RcFile } from 'antd/lib/upload';
import Button from 'components/new-ui/base/button/Button';
import UploadDocuments, { UploadDocumentsRef } from 'components/new-ui/composed/upload-documents/UploadDocuments';
import { H2 } from 'components/new-ui/base/typography/styles';
import FilesTable, { FileRecord } from 'components/new-ui/composed/table/FilesTable';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import useRequest from 'hooks/useRequest';
import useDeclarations from 'hooks/useDeclarations';
import useFormUtils from 'hooks/useFormUtils';
import useSession from 'hooks/useSession';
import useCodelists from 'hooks/useCodelists';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { useParams } from 'react-router-dom';
import { uploadFile } from 'utils/uploadFile';
import { triggerBrowserDownload } from 'utils/download-utils';
import { getErrorMessage } from 'utils/getErrorMessage';
import { getFileExtensionFromMime } from 'utils/file-types';
import {
    confirmUploadFile,
    documentsPreUpload,
    downloadFile,
    downloadFile as downloadFilePreview,
} from 'store/documents/client';
import {
    createDocumentUk,
    submitDocumentsAes,
    submitDocumentsAis,
    submitDocumentsNcts,
    submitDocumentsUk,
} from 'store/file-upload/client';
import { DeclarationCountry, getDeclrationCountry } from 'store/declarations/enums/common/declaration-country';
import { getDeclarationType, getGeneratedDeclarationDocument } from 'views/declarations/utils/declaration-utils';
import { DeclarationDocumentResponse } from 'store/declarations/declaration';
import DeclarationDocumentFormModal from 'views/declarations/common/confirm-document-modal/DeclarationDocumentFormModal';
import Notification from 'components/ui/base/notification/Notification';
import { DeclarationDocument, DeclarationDocumentStatus } from './types';
import { isDeclarationDocumentComplete } from './utils';
import { validateDeclarationFile } from 'store/file-upload/utils';
import { deleteDeclarationDocument } from 'store/declarations/client';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';

export type DeclarationFile = DeclarationDocumentResponse & {
    status: DeclarationDocumentStatus;
    createdBy: string;
    isInfoComplete: boolean;
    declarationFileId?: string;
};

const SAD_DOCUMENT_ID = 'generated-sad-document';

const Documentation = () => {
    const { data: preUploadData, doRequest: preUpload } = useRequest(documentsPreUpload);
    const { customerId } = useSession();
    const { declarationId } = useParams<{ declarationId: string }>();
    const { isAis, isAes, isNcts, isICS2, countryLowerCase: country } = useFormUtils();
    const { aisCodelists, aesCodelists, nctsCodelists, getCodeLists } = useCodelists();
    const { hideGlobalOverlay, showGlobalOverlay } = useGlobalOverlay();
    const {
        declaration,
        documents: cmsDocuments,
        listDeclarationDocuments: listCmsFiles,
        getDeclaration,
    } = useDeclarations();

    const { data: sadDraft, doRequest: fetchSadDraft } = useRequest(getGeneratedDeclarationDocument);
    const [sadDocUrl, setSadDocUrl] = useState<string>();
    const [hasFetchedGeneratedDocs, setHasFetchedGeneratedDocs] = useState(false);

    const [fileToConfirm, setFileToConfirm] = useState<DeclarationFile | undefined>(undefined);
    const [fileToDelete, setFileToDelete] = useState<DeclarationFile | undefined>(undefined);
    const [fileToEdit, setFileToEdit] = useState<DeclarationFile | undefined>(undefined);
    const [declarationDocuments, setDeclarationDocuments] = useState<DeclarationDocument[] | undefined>(
        declaration?.uploadedFiles
    );
    const [isDeleteFileConfirmationModalOpen, setIsDeleteFileConfirmationModalOpen] = useState(false);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);
    const [isSubmittingFiles, setIsSubmittingFiles] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const uploaderRef = useRef<UploadDocumentsRef>(null);

    const documentTypeCodelists =
        isAes && aesCodelists
            ? [
                  ...aesCodelists.transportDocumentType,
                  ...aesCodelists.supportingDocumentType,
                  ...aesCodelists.previousDocumentType,
              ]
            : isNcts && nctsCodelists
            ? [
                  ...(nctsCodelists.previousDocumentType ?? []),
                  ...(nctsCodelists.supportingDocumentType ?? []),
                  ...(nctsCodelists.transportDocumentType ?? []),
              ]
            : aisCodelists?.commonDocumentsType;

    const fetchDeclarationDocuments = useCallback(async () => {
        if (!declarationId) return;

        try {
            setIsUploadingDocument(true);
            await Promise.all([listCmsFiles(declarationId), getDeclaration(declarationId)]);
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Could not refresh declaration documents.',
                placement: 'top',
            });
        } finally {
            setIsUploadingDocument(false);
        }
    }, [declarationId, listCmsFiles, getDeclaration]);

    const handleUploadDocumentsClick = () => {
        uploaderRef.current?.triggerUpload();
    };

    const disabledSubmit: boolean = useMemo(() => {
        if (isAis || isAes) return declaration?.mrn === undefined;
        return false;
    }, [declaration?.mrn, isAis, isAes]);

    const handleDocumentPreview = async (file?: FileRecord) => {
        if (!file) {
            return Notification({
                type: 'error',
                messageTitle: 'Preview Error',
                description: 'Could not detect file for preview.',
                placement: 'top',
            });
        }

        if (file.id === SAD_DOCUMENT_ID) {
            const url = sadDocUrl;

            if (!url) {
                return Notification({
                    type: 'error',
                    messageTitle: 'Preview Error',
                    description: 'Document is not ready. Please try again shortly.',
                    placement: 'top',
                });
            }

            window.open(url, '_blank');
            return;
        }

        try {
            const documentPreview = await downloadFilePreview(file.id);

            if (!documentPreview.preSignedUrl) {
                return Notification({
                    type: 'error',
                    messageTitle: 'Preview Error',
                    description: 'Document is not ready. Please try again shortly.',
                    placement: 'top',
                });
            }

            window.open(documentPreview.preSignedUrl, '_blank');
        } catch {
            Notification({
                type: 'error',
                messageTitle: 'Preview Error',
                description: 'Could not generate document preview. Please try again shortly.',
                placement: 'top',
            });
        }
    };

    const handleDocumentDownload = async (file?: FileRecord) => {
        if (!file) {
            return Notification({
                type: 'error',
                messageTitle: 'Download Error',
                description: 'Could not detect file for download.',
                placement: 'top',
            });
        }

        if (file.id === SAD_DOCUMENT_ID) {
            const draft = sadDraft;

            if (!draft) {
                return Notification({
                    type: 'error',
                    messageTitle: 'Download Error',
                    description: 'Document is not ready. Please try again shortly.',
                    placement: 'top',
                });
            }

            triggerBrowserDownload(draft.blob, file.filename || 'document');
            return;
        }

        try {
            const { preSignedUrl } = await downloadFile(file.id);

            if (!preSignedUrl) {
                return Notification({
                    type: 'error',
                    messageTitle: 'Download Error',
                    description: 'Document is not ready. Please try again shortly.',
                    placement: 'top',
                });
            }

            const response = await fetch(preSignedUrl);
            const blob = await response.blob();
            triggerBrowserDownload(blob, file.filename || 'document');
        } catch {
            Notification({
                type: 'error',
                messageTitle: 'Download Error',
                description: 'Could not download document. Please try again shortly.',
                placement: 'top',
            });
        }
    };

    const handleRowSelectionChange = (keys: React.Key[]) => {
        setSelectedRowKeys(keys);
    };

    // CMS holds file-level metadata (contentType, fileSize, link) while declaration.uploadedFiles holds the
    // declaration-specific relationship (status, createdBy, fileInfo, and the id delete/update rely on). Only
    // CMS files with a matching declaration document are shown, since files without that association aren't
    // actually attached to the declaration yet and can't be deleted/submitted.
    const declarationFiles = useMemo<DeclarationFile[] | []>(() => {
        const files: DeclarationFile[] = [];
        if (!cmsDocuments?.list) return files;

        for (const doc of cmsDocuments.list) {
            const matchedDeclarationDocument = (declarationDocuments ?? []).find((decl) => decl.fileId === doc.id);

            if (!matchedDeclarationDocument) continue;

            files.push({
                ...doc,
                declarationFileId: matchedDeclarationDocument?.id,
                status: matchedDeclarationDocument.status ?? 'UPLOADED',
                createdBy: matchedDeclarationDocument.createdBy ?? '-',
                isInfoComplete: isDeclarationDocumentComplete(doc, matchedDeclarationDocument, isAes, isAis, isNcts),
            });
        }

        return files.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    }, [cmsDocuments?.list, declarationDocuments, isAes, isAis, isNcts]);

    const mappedDeclarationFiles = useMemo(
        () =>
            declarationFiles.map((file) => ({
                id: file.id ?? '-',
                filename: file.filename ?? '-',
                createdAt: file.createdAt ?? '-',
                fileType: getFileExtensionFromMime(file.contentType) ?? 'Unknown file type',
                uploadedBy: file.createdBy ?? '-',
                status: file.status,
                fileSize: file.fileSize,
                documentType: file.documentType,
                isInfoComplete: file.isInfoComplete,
                declarationFileId: file.declarationFileId,
            })),
        [declarationFiles]
    );

    const generatedDocumentCreatedAt = declaration?.submittedAt ?? declaration?.updatedAt ?? declaration?.createdAt;

    const generatedDocumentRows = useMemo<FileRecord[]>(() => {
        const rows: FileRecord[] = [];

        if (sadDraft && generatedDocumentCreatedAt) {
            const filename = sadDraft.fileName ?? 'sad-document';
            rows.push({
                id: SAD_DOCUMENT_ID,
                filename: sadDraft.status === 'DRAFT' ? `${filename} (Draft)` : filename,
                fileType: 'pdf',
                uploadedBy: '-',
                createdAt: generatedDocumentCreatedAt,
                status: 'GENERATED',
            });
        }

        return rows;
    }, [sadDraft, generatedDocumentCreatedAt]);

    const combinedFiles = useMemo(
        () => [...generatedDocumentRows, ...mappedDeclarationFiles],
        [generatedDocumentRows, mappedDeclarationFiles]
    );

    const paginatedFiles = useMemo(() => {
        if (!hasFetchedGeneratedDocs) return [];

        const start = (pagination.current - 1) * pagination.pageSize;
        const end = start + pagination.pageSize;
        return combinedFiles.slice(start, end);
    }, [combinedFiles, pagination, hasFetchedGeneratedDocs]);

    const declarationDocument: DeclarationDocument | undefined = useMemo(
        () => declarationDocuments?.find((file) => file.fileId === fileToEdit?.id),
        [declarationDocuments, fileToEdit?.id]
    );

    const handleSubmitFilesToCustoms = useCallback(async () => {
        if (!selectedRowKeys.length) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Please select at least one file for submission.',
                placement: 'top',
            });
            return;
        }

        if (selectedRowKeys.length > 10 && country === DeclarationCountry.UK) {
            Notification({
                type: 'error',
                messageTitle: 'Submission Failed',
                description: "Can't submit more than 10 documents at once.",
                placement: 'top',
            });
            return;
        }

        if (!declarationId) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing declaration ID.',
                placement: 'top',
            });
            return;
        }

        const hasIncompleteSelection = declarationFiles.some(
            (file) => selectedRowKeys.includes(file.id) && !file.isInfoComplete
        );

        if (hasIncompleteSelection) {
            Notification({
                type: 'error',
                messageTitle: 'Submission Failed',
                description: 'Please complete all required document details before submitting.',
                placement: 'top',
            });
            return;
        }

        setIsSubmittingFiles(true);

        try {
            if (country === DeclarationCountry.IRELAND) {
                const submitDocument = isAes ? submitDocumentsAes : isAis ? submitDocumentsAis : submitDocumentsNcts;
                await submitDocument({ documentIds: selectedRowKeys }, declarationId);
            } else if (country === DeclarationCountry.UK) {
                await submitDocumentsUk({ documentIds: selectedRowKeys }, declarationId);
            }

            Notification({
                type: 'success',
                messageTitle: 'Documents submitted',
                description: 'Successfully submitted files to customs.',
                placement: 'top',
            });

            setSelectedRowKeys([]);
            await fetchDeclarationDocuments();
        } catch (error) {
            const err = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Submission Failed',
                description: err || 'Could not submit documents. Please try again.',
                placement: 'top',
            });
        } finally {
            setIsSubmittingFiles(false);
        }
    }, [country, declarationId, isAes, isAis, fetchDeclarationDocuments, selectedRowKeys, declarationFiles]);

    const closeDeleteModal = () => {
        setIsDeleteFileConfirmationModalOpen(false);
        setFileToDelete(undefined);
    };

    const handleConfirmFileDelete = async (file?: DeclarationFile) => {
        if (!file || !declarationId || !file.declarationFileId) {
            console.error('Cannot delete document, missing required fields:', {
                file,
                declarationId,
                declarationFileId: file?.declarationFileId,
            });
            Notification({
                type: 'error',
                messageTitle: 'Deletion Failed',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
            return;
        }

        try {
            await deleteDeclarationDocument(declarationId, file.declarationFileId);
            await fetchDeclarationDocuments();
            closeDeleteModal();

            Notification({
                type: 'success',
                messageTitle: 'Success',
                description: 'Successfully deleted file.',
                placement: 'top',
            });
        } catch (err) {
            closeDeleteModal();

            const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Deletion Failed',
                description: errorMessage,
                placement: 'top',
            });
        }
    };

    // Global overlay for submission
    useEffect(() => {
        if (isSubmittingFiles) {
            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Submitting documents',
                },
            });
        } else {
            hideGlobalOverlay();
        }
    }, [hideGlobalOverlay, isSubmittingFiles, showGlobalOverlay]);

    useEffect(() => {
        if (declarationId) {
            fetchDeclarationDocuments();
        }
    }, [declarationId, fetchDeclarationDocuments]);

    // Load the codes list
    useEffect(() => {
        if (declaration) {
            const declarationCountry = getDeclrationCountry(declaration);
            const declarationType = getDeclarationType(declaration);
            getCodeLists(declarationCountry, declarationType, declaration.customerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declaration?.id]);

    // Fetch the generated SAD document, when available for this declaration
    useEffect(() => {
        if (declaration) {
            fetchSadDraft('SAD', declaration)
                .then((draft) => setSadDocUrl(draft ? window.URL.createObjectURL(draft.blob) : undefined))
                .finally(() => setHasFetchedGeneratedDocs(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declaration?.id, declaration?.status]);

    useEffect(() => {
        if (declaration?.uploadedFiles) {
            setDeclarationDocuments(declaration.uploadedFiles);
        } else {
            setDeclarationDocuments(undefined);
        }
    }, [declaration?.uploadedFiles]);

    return (
        <div>
            <Row
                justify="space-between"
                align="middle"
                style={{
                    marginBottom: 16,
                }}
            >
                <H2>Documents</H2>
                {!isUploadingDocument && !isICS2 && (
                    <Button onClick={handleUploadDocumentsClick} icon={<UploadOutlined />}>
                        Upload Documents
                    </Button>
                )}
            </Row>

            <UploadDocuments
                disabled={isICS2}
                method="PUT"
                multiple={false}
                ref={uploaderRef}
                showUploadList={false}
                beforeUpload={(file: RcFile) => {
                    return validateDeclarationFile(
                        file,
                        customerId,
                        declarationId,
                        preUpload,
                        isAis,
                        showGlobalOverlay
                    );
                }}
                onChange={async ({ file }) => {
                    if (!preUploadData?.preSignedUrl || !declarationId || !customerId) {
                        Notification({
                            type: 'error',
                            messageTitle: 'Upload error',
                            description: 'Missing upload metadata. Please try again.',
                            placement: 'top',
                        });
                        hideGlobalOverlay();
                        return;
                    }

                    try {
                        setIsUploadingDocument(true);

                        await uploadFile(file as unknown as File, preUploadData.preSignedUrl);

                        const match = preUploadData.preSignedUrl.match(/\/([^/?]+)\?/);
                        const uuid = match?.[1];

                        if (!uuid) {
                            throw new Error('Missing file identifier');
                        }

                        const { payload: cmsFile } = await confirmUploadFile({
                            s3id: uuid,
                            fileName: file.name,
                            fileSize: file.size,
                            customerId,
                            declarationId,
                        });

                        if (country === DeclarationCountry.IRELAND) {
                            setFileToConfirm(cmsFile);
                        } else if (country === DeclarationCountry.UK) {
                            await createDocumentUk(
                                {
                                    fileId: cmsFile.id,
                                    fileReference: cmsFile.link,
                                    fileName: cmsFile.filename,
                                },
                                declarationId
                            );
                        }

                        await fetchDeclarationDocuments();
                    } catch (error) {
                        Notification({
                            type: 'error',
                            messageTitle: 'Upload error',
                            description: `${file.name} file upload failed.`,
                            placement: 'top',
                        });
                    } finally {
                        setIsUploadingDocument(false);
                        hideGlobalOverlay();
                    }
                }}
            />

            <Row
                justify="space-between"
                align="middle"
                style={{
                    marginTop: 24,
                    marginBottom: 8,
                }}
            >
                <Col>
                    <H2>Uploaded Documents</H2>
                </Col>
                {!disabledSubmit && selectedRowKeys.length > 0 && (
                    <Col>
                        <Button loading={isSubmittingFiles} onClick={handleSubmitFilesToCustoms}>
                            Submit to Revenue
                        </Button>
                    </Col>
                )}
            </Row>

            <FilesTable
                showUploadedByColumn={false}
                loading={isUploadingDocument || !hasFetchedGeneratedDocs}
                dataSource={paginatedFiles}
                rowSelection={
                    disabledSubmit
                        ? undefined
                        : {
                              type: 'checkbox',
                              selectedRowKeys,
                              onChange: handleRowSelectionChange,
                              getCheckboxProps: (record: any) => ({
                                  disabled:
                                      record.status === 'SUBMITTED' ||
                                      record.status === 'GENERATED' ||
                                      !record.isInfoComplete,
                              }),
                              renderCell: (_checked, record: any, _index, originNode) => {
                                  const isDisabled =
                                      record.status === 'SUBMITTED' ||
                                      record.status === 'GENERATED' ||
                                      !record.isInfoComplete;
                                  if (!isDisabled) return originNode;

                                  const tooltip =
                                      record.status === 'SUBMITTED'
                                          ? 'This file has already been submitted.'
                                          : record.status === 'GENERATED'
                                          ? 'This document is generated automatically and cannot be submitted.'
                                          : 'Please complete all required document details before submitting.';

                                  return <Tooltip title={tooltip}>{originNode}</Tooltip>;
                              },
                          }
                }
                showStatusColumn
                pagination={{
                    position: ['bottomCenter'],
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: hasFetchedGeneratedDocs ? combinedFiles.length : 0,
                    hideOnSinglePage: true,
                    onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
                }}
                actions={{
                    delete: (file) => {
                        setFileToDelete(file);
                        setIsDeleteFileConfirmationModalOpen(true);
                    },
                    download: handleDocumentDownload,
                    preview: handleDocumentPreview,
                    edit: (file) => setFileToEdit(file),
                }}
            />

            <ConfirmationModal
                type="error"
                title="Delete Document?"
                confirmButtonText="Delete"
                content={
                    <Typography.Text>
                        The document will be deleted and all information associated with it will be lost
                    </Typography.Text>
                }
                open={isDeleteFileConfirmationModalOpen}
                onConfirm={() => handleConfirmFileDelete(fileToDelete)}
                onCancel={() => {
                    setFileToDelete(undefined);
                    setIsDeleteFileConfirmationModalOpen(false);
                }}
            />

            <DeclarationDocumentFormModal
                open={Boolean(fileToConfirm) || Boolean(fileToEdit)}
                isEditing={Boolean(fileToEdit)}
                width={820}
                onOk={() => {
                    setFileToConfirm(undefined);
                    setFileToEdit(undefined);
                }}
                onCancel={() => {
                    setFileToConfirm(undefined);
                    setFileToEdit(undefined);
                }}
                file={fileToConfirm || fileToEdit}
                declarationDocument={declarationDocument}
                declarationId={declarationId}
                documentTypeOptions={documentTypeCodelists}
                listCmsFiles={fetchDeclarationDocuments}
            />
        </div>
    );
};

export default Documentation;
